// use this reducer for UI and user activity state (not data from the backend)
import Cookies from 'cookies-js';
// @ts-ignore: missing type declaration file
import { CookieStorage } from 'redux-persist-cookie-storage';
import { createMigrate, persistReducer } from 'redux-persist';
import { type RootState } from 'app/store';
import { type AppState, type AppActionTypes } from 'app/store/types';
import { getRootDomain } from 'app/utils/helpers';

const initialState: AppState = {
	selectedLicenseGroupId: undefined!, // HACK::assume never undefined and handle check only in <LicenseGroupPageWrapper />
	queuesFilterText: '',
	usersFilterText: '',
	devicesFilterText: '',
	archiveFilterText: '',
	myFilesFilterText: '',
	trashFilterText: '',
	jobNamesFilterText: '',
	bsnFilterText: '',
	bsnDeviceFilterText: '',
	bsnDeviceGroupFilterText: '',
	bsnPolicyGroupFilterText: '',
	bsnMonitorFilterText: '',
	appsFilterText: '',
	printsFilterText: '',
	printGroupsFilterText: '',
	notificationsFilterText: '',
	rolesFilterText: '',
	logsFilterText: '',
	formsFilterText: '',
	workflowsFilterText: '',
	managedLicenseGroupsFilterText: '',
	logSection: '',
	logSubSection: '',
	logDateOption: '',
	logCustomDates: { start: null, end: null },
	logLevelOptions: [''],
	hideNavbar: {},
	cookieNagbarAcknowledged: false,
	cookieSettings: false,
	hideExpiredBanner: false,
	hideSuspendedBanner: false,
	hideMFABanner: false,
	hideExpiredScreen: false,
	siteBackgroundCache: 0,
	appIconCache: {},
	modifiedTenantPricing: {
		currentPrice: undefined,
		modifiedPrice: undefined,
	},
	mfpDashboardTableFilters: {
		order: { direction: 'asc', id: 'name' },
		filters: {
			status: { online: true, offline: true, error: true },
			toner: { normal: true, low: true, empty: true },
			paper: { normal: true, low: true, empty: true },
			favoriteOnly: { checked: false },
		},
		columnVisibility: {
			name: true,
			group: true,
			serial: true,
			ip: true,
			description: true,
			prints: true,
			copies: true,
			scans: true,
			lastActivity: true,
		},
	},
};

const appReducer = (state = initialState, action: AppActionTypes) => {
	switch (action.type) {
		case 'UPDATE_MFP_DASHBOARD_TABLE_FILTERS': {
			return {
				...state,
				mfpDashboardTableFilters: {
					...state.mfpDashboardTableFilters,
					...action.payload,
				},
			};
		}
		case 'GET_SELECTED_LICENSE_GROUP_ID_BY_SLUG': {
			return {
				...state,
				selectedLicenseGroupId: action.payload.selectedLicenseGroupId,
			};
		}
		case 'SET_QUEUES_FILTER_TEXT': {
			return {
				...state,
				queuesFilterText: action.payload.queuesFilterText,
			};
		}
		case 'SET_USERS_FILTER_TEXT': {
			return {
				...state,
				usersFilterText: action.payload.usersFilterText,
			};
		}
		case 'SET_DEVICES_FILTER_TEXT': {
			return {
				...state,
				devicesFilterText: action.payload.devicesFilterText,
			};
		}
		case 'SET_ARCHIVE_FILTER_TEXT': {
			return {
				...state,
				archiveFilterText: action.payload.archiveFilterText,
			};
		}
		case 'SET_MY_FILES_FILTER_TEXT': {
			return {
				...state,
				myFilesFilterText: action.payload.myFilesFilterText,
			};
		}
		case 'SET_TRASH_FILTER_TEXT': {
			return {
				...state,
				trashFilterText: action.payload.trashFilterText,
			};
		}
		case 'SET_APPS_FILTER_TEXT': {
			return {
				...state,
				appsFilterText: action.payload.appsFilterText,
			};
		}
		case 'SET_PRINTS_FILTER_TEXT': {
			return {
				...state,
				printsFilterText: action.payload.printsFilterText,
			};
		}
		case 'SET_PRINT_GROUPS_FILTER_TEXT': {
			return {
				...state,
				printGroupsFilterText: action.payload.printGroupsFilterText,
			};
		}
		case 'SET_NOTIFICATIONS_FILTER_TEXT': {
			return {
				...state,
				notificationsFilterText: action.payload.notificationsFilterText,
			};
		}
		case 'SET_JOB_NAMES_FILTER_TEXT': {
			return {
				...state,
				jobNamesFilterText: action.payload.jobNamesFilterText,
			};
		}
		case 'SET_BSN_FILTER_TEXT': {
			return {
				...state,
				bsnFilterText: action.payload.bsnFilterText,
			};
		}
		case 'SET_BSN_DEVICE_FILTER_TEXT': {
			return {
				...state,
				bsnDeviceFilterText: action.payload.bsnDeviceFilterText,
			};
		}
		case 'SET_BSN_DEVICE_GROUP_FILTER_TEXT': {
			return {
				...state,
				bsnDeviceGroupFilterText: action.payload.bsnDeviceGroupFilterText,
			};
		}
		case 'SET_BSN_POLICY_GROUP_FILTER_TEXT': {
			return {
				...state,
				bsnPolicyGroupFilterText: action.payload.bsnPolicyGroupFilterText,
			};
		}
		case 'SET_BSN_MONITOR_FILTER_TEXT': {
			return {
				...state,
				bsnMonitorFilterText: action.payload.bsnMonitorFilterText,
			};
		}
		case 'SET_ROLES_FILTER_TEXT': {
			return {
				...state,
				rolesFilterText: action.payload.rolesFilterText,
			};
		}
		case 'SET_LOGS_FILTER_TEXT': {
			return {
				...state,
				logsFilterText: action.payload.logsFilterText,
			};
		}
		case 'SET_FORMS_FILTER_TEXT': {
			return {
				...state,
				formsFilterText: action.payload.formsFilterText,
			};
		}
		case 'SET_WORKFLOWS_FILTER_TEXT': {
			return {
				...state,
				workflowsFilterText: action.payload.workflowsFilterText,
			};
		}
		case 'SET_MANAGED_LICENSE_GROUPS_FILTER_TEXT': {
			return {
				...state,
				managedLicenseGroupsFilterText: action.payload.managedLicenseGroupsFilterText,
			};
		}
		case 'SET_LOG_SECTION': {
			return {
				...state,
				logSection: action.payload.logSection,
			};
		}
		case 'SET_LOG_SUB_SECTION': {
			return {
				...state,
				logSubSection: action.payload.logSubSection,
			};
		}
		case 'SET_LOG_DATE_OPTION': {
			return {
				...state,
				logDateOption: action.payload.logDateOption,
			};
		}
		case 'SET_LOG_CUSTOM_DATES': {
			return {
				...state,
				logCustomDates: action.payload.logCustomDates,
			};
		}
		case 'SET_LOG_LEVEL_OPTIONS': {
			return {
				...state,
				logLevelOptions: action.payload.logLevelOptions,
			};
		}
		case 'HIDE_NAVBAR': {
			const { name, hide } = action.payload;

			return {
				...state,
				hideNavbar: { ...state.hideNavbar, [name]: hide },
			};
		}
		case 'ACKNOWLEDGE_COOKIE_NAGBAR': {
			return {
				...state,
				cookieNagbarAcknowledged: true,
			};
		}
		case 'SET_COOKIE_SETTINGS': {
			return {
				...state,
				cookieSettings: action.payload.cookieSettings,
			};
		}
		case 'HIDE_EXPIRED_BANNER': {
			return {
				...state,
				hideExpiredBanner: true,
			};
		}
		case 'HIDE_SUSPENDED_BANNER': {
			return {
				...state,
				hideSuspendedBanner: true,
			};
		}
		case 'HIDE_EXPIRED_SCREEN': {
			return {
				...state,
				hideExpiredScreen: true,
			};
		}
		case 'BUMP_SITE_BACKGROUND_CACHE': {
			return {
				...state,
				siteBackgroundCache: state.siteBackgroundCache + 1,
			};
		}
		case 'BUMP_APP_ICON_CACHE': {
			return {
				...state,
				appIconCache: {
					...state.appIconCache,
					[action.payload.appId]: (state.appIconCache[action.payload.appId] ?? 0) + 1,
				},
			};
		}
		// wipe selectedLicenseGroupId if id not found in returned license groups list
		// case 'GET_ADMINED_LICENSE_GROUPS_SUCCESS': {
		// 	const prevSelectedLicenseGroupId = state.selectedLicenseGroupId as string | undefined; // HACK::simplifying type by just being string but it can be both here

		// 	return {
		// 		...state,
		// 		selectedLicenseGroupId:
		// 			prevSelectedLicenseGroupId && action.payload.data?.[prevSelectedLicenseGroupId]
		// 				? prevSelectedLicenseGroupId
		// 				: undefined!
		// 	};
		// }
		case 'GET_MODIFIED_TENANT_PRICING_SUCCESS': {
			const { currentPrice, modifiedPrice } = action.payload;

			return {
				...state,
				modifiedTenantPricing: {
					currentPrice,
					modifiedPrice,
				},
			};
		}
		case 'LOGGED_OUT_USER':
			return {
				...initialState,
				cookieNagbarAcknowledged: state.cookieNagbarAcknowledged,
				hideNavbar: state.hideNavbar,
			};
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

const cookieStorageOptions = {
	expiration: {
		default: 2147483647,
	},
	setCookieOptions: {
		// DEV NOTE::cookies will not work for localhost (_could_ probably set up a HOST domain for this to work locally?)
		domain: process.env.NODE_ENV === 'development' ? undefined : getRootDomain(),
	},
};

const migrations = {
	1: (state: any) => {
		if (!state) return state;

		return {
			...state,
			mfpDashboardTableFilters: {
				...state.mfpDashboardTableFilters,
				filters: {
					...state.mfpDashboardTableFilters?.filters,
					favoriteOnly: state.mfpDashboardTableFilters?.filters?.favoriteOnly ?? {
						checked: false,
					},
				},
			},
		};
	},
};

export default persistReducer(
	{
		key: 'app',
		version: 1,
		storage: new CookieStorage(Cookies, cookieStorageOptions),
		whitelist: ['cookieNagbarAcknowledged', 'siteBackgroundCache', 'appIconCache', 'mfpDashboardTableFilters'], // wipe most app state on refresh
		migrate: createMigrate(migrations, { debug: false }),
	},
	appReducer,
);

// Selectors
export const getSelectedLicenseGroupId = ({ app }: RootState) => app.selectedLicenseGroupId;
export const getQueuesFilterText = ({ app }: RootState) => app.queuesFilterText;
export const getUsersFilterText = ({ app }: RootState) => app.usersFilterText;
export const getDevicesFilterText = ({ app }: RootState) => app.devicesFilterText;
export const getJobNamesFilterText = ({ app }: RootState) => app.jobNamesFilterText;
export const getBsnFilterText = ({ app }: RootState) => app.bsnFilterText;
export const getBsnDeviceFilterText = ({ app }: RootState) => app.bsnDeviceFilterText;
export const getBsnDeviceGroupFilterText = ({ app }: RootState) => app.bsnDeviceGroupFilterText;
export const getBsnPolicyGroupFilterText = ({ app }: RootState) => app.bsnPolicyGroupFilterText;
export const getBsnMonitorFilterText = ({ app }: RootState) => app.bsnMonitorFilterText;
export const getAppsFilterText = ({ app }: RootState) => app.appsFilterText;
export const getPrintsFilterText = ({ app }: RootState) => app.printsFilterText;
export const getPrintGroupsFilterText = ({ app }: RootState) => app.printGroupsFilterText;
export const getNotificationsFilterText = ({ app }: RootState) => app.notificationsFilterText;
export const getRolesFilterText = ({ app }: RootState) => app.rolesFilterText;
export const getLogsFilterText = ({ app }: RootState) => app.logsFilterText;
export const getFormsFilterText = ({ app }: RootState) => app.formsFilterText;
export const getWorkflowsFilterText = ({ app }: RootState) => app.workflowsFilterText;
export const getManagedLicenseGroupsFilterText = ({ app }: RootState) => app.managedLicenseGroupsFilterText;
export const getLogSection = ({ app }: RootState) => app.logSection;
export const getLogSubSection = ({ app }: RootState) => app.logSubSection;
export const getLogDateOption = ({ app }: RootState) => app.logDateOption;
export const getLogCustomDates = ({ app }: RootState) => app.logCustomDates;
export const getLogLevelOptions = ({ app }: RootState) => app.logLevelOptions;
export const getHideNavbar = ({ app }: RootState) => Object.values(app.hideNavbar).some((hide) => hide);
export const getCookieNagbarAcknowledged = ({ app }: RootState) => app.cookieNagbarAcknowledged;
export const getCookieSettings = ({ app }: RootState) => app.cookieSettings;
export const getHideExpiredBanner = ({ app }: RootState) => app.hideExpiredBanner;
export const getHideSuspendedBanner = ({ app }: RootState) => app.hideSuspendedBanner;
export const getHideMFABanner = ({ app }: RootState) => app.hideMFABanner;
export const getHideExpiredScreen = ({ app }: RootState) => app.hideExpiredScreen;
export const getSiteBackgroundCache = ({ app }: RootState) => app.siteBackgroundCache;
export const getAppIconCache = ({ app }: RootState) => app.appIconCache;
export const getModifiedTenantPricing = ({ app }: RootState) => app.modifiedTenantPricing;
export const getMfpDashboardTableFilters = ({ app }: RootState) => app.mfpDashboardTableFilters;
